<template>
  <div>
    <b-card>
      <b-card-header>
        <h5>{{ selectedReportKey ? currentReportConfig.label : 'Warehouse Reports' }}</h5>
      </b-card-header>
      <b-card-body>
        <validation-observer ref="observer">
          <b-form @submit.prevent="generateReport">
            <b-row>
              <b-col cols="12">
                <b-form-group label="Report">
                  <validation-provider
                    name="Report"
                    rules="required"
                  >
                    <b-form-select
                      id="report_key"
                      v-model="selectedReportKey"
                      :options="reportOptions"
                      value-field="key"
                      text-field="label"
                      class="text-capitalize"
                    >
                      <b-form-select-option :value="null">
                        Select report...
                      </b-form-select-option>
                    </b-form-select>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <template v-if="selectedReportKey">
              <b-row :key="parametersRefreshKey">
                <b-col cols="6">
                  <b-form-group label="Region">
                    <validation-provider
                      v-if="isSuperUser"
                      name="Region"
                      rules="required"
                    >
                      <b-form-select
                        id="region_id"
                        v-model="filter.region_id"
                        :options="regionOptions"
                        value-field="id"
                        text-field="name"
                        class="text-capitalize"
                        @input="changeRegion"
                      >
                        <b-form-select-option :value="null">
                          Select region...
                        </b-form-select-option>
                      </b-form-select>
                    </validation-provider>
                    <div
                      v-else
                      class="text-muted small mb-0"
                    >
                      Location is set to your assigned station.
                    </div>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group label="Warehouse">
                    <validation-provider
                      name="Warehouse"
                      rules="required"
                    >
                      <b-form-select
                        id="warehouse_id"
                        v-model="filter.warehouse_id"
                        :options="warehouseOptions"
                        value-field="id"
                        text-field="name"
                        class="text-capitalize"
                        :multiple="allowsMultipleWarehouses"
                      >
                        <b-form-select-option :value="null">
                          Select warehouse...
                        </b-form-select-option>
                      </b-form-select>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <template v-if="requiresDateRange">
                  <b-col cols="3">
                    <b-form-group label="Start date">
                      <validation-provider
                        name="Start date"
                        rules="required"
                      >
                        <b-form-datepicker
                          id="start_date"
                          v-model="filter.start_date"
                          class="w-100"
                          :max="filter.end_date || null"
                          placeholder="Select Start Date"
                        />
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col cols="3">
                    <b-form-group label="End date">
                      <validation-provider
                        name="End date"
                        rules="required"
                      >
                        <b-form-datepicker
                          id="end_date"
                          v-model="filter.end_date"
                          class="w-100"
                          :min="filter.start_date || null"
                          placeholder="Select End Date"
                        />
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </template>

                <b-col
                  v-if="supportsTfSummaryReport"
                  cols="12"
                  md="3"
                >
                  <b-form-group label="Report view">
                    <b-form-checkbox
                      v-model="showTfSummary"
                      switch
                    >
                      Summary by TF
                    </b-form-checkbox>
                    <small class="text-muted">
                      {{ showTfSummary ? 'Grouped by TF number' : 'Detailed response records' }}
                    </small>
                  </b-form-group>
                </b-col>

                <b-col
                  v-if="supportsStackSummaryReport"
                  cols="12"
                  md="3"
                >
                  <b-form-group label="Report view">
                    <b-form-checkbox
                      v-model="showStackSummary"
                      switch
                    >
                      Summary by Stack
                    </b-form-checkbox>
                    <small class="text-muted">
                      {{ showStackSummary ? 'Grouped by stack' : 'Detailed barcode records' }}
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
            </template>

            <b-row class="mt-3">
              <b-col
                cols="12"
                class="text-right"
              >
                <b-button
                  type="submit"
                  variant="success"
                  :disabled="isGenerateDisabled"
                >
                  <b-spinner
                    v-if="loading"
                    small
                    label="Loading"
                  />
                  <span>{{ loading ? 'Generating...' : 'Generate report' }}</span>
                </b-button>
                <b-button
                  variant="info"
                  class="ml-2"
                  :disabled="loading || !records.length"
                  @click="exportPdf"
                >
                  Export PDF
                </b-button>
                <b-button
                  variant="primary"
                  class="ml-2"
                  :disabled="loading || !tableItems.length"
                  @click="exportExcel"
                >
                  Export Excel
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>

        <b-alert
          v-if="errorMessage"
          variant="danger"
          show
          class="mt-3"
        >
          {{ errorMessage }}
        </b-alert>

        <template v-if="selectedReportKey">
          <div
            v-if="warehouseSummaryItems.length"
            class="mt-3"
          >
            <b-row>
              <b-col
                v-for="item in warehouseSummaryItems"
                :key="item.label"
                cols="6"
                md="4"
              >
                <div class="text-muted small">
                  {{ item.label }}
                </div>
                <div>{{ item.value }}</div>
              </b-col>
            </b-row>
          </div>
          <b-table
            striped
            hover
            small
            responsive
            class="mt-3"
            :items="tableItems"
            :fields="currentColumns"
            :empty-text="`No ${currentReportConfig.label.toLowerCase()} found`"
            :tbody-tr-class="tbodyTrClass"
            :sort-compare="sortCompare"
          />

          <div class="mt-2 text-muted">
            <span v-if="isTfSummaryMode">
              Total TFs: {{ displayedRecordCount }} | Source records: {{ totalRecords }}
            </span>
            <span v-else-if="isStackSummaryMode">
              Total stacks: {{ displayedRecordCount }} | Source records: {{ totalRecords }}
            </span>
            <span v-else-if="totalRecords !== null">Total records: {{ totalRecords }}</span>
          </div>
        </template>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BForm,
    BFormGroup,
    BButton,
    BFormCheckbox,
    BFormSelect,
    BFormSelectOption,
    BFormDatepicker,
    BAlert,
    BSpinner,
} from 'bootstrap-vue'
import store from '@/store'
import warehouseReportsStoreModule from '@/views/cromis/reports/warehouse_reports/warehouseReportsStoreModule'
import useWarehouseReportsList from '@/views/cromis/reports/warehouse_reports/useWarehouseReportsList'
import {
    buildWarehouseReportColumns,
    buildWarehouseReportTotalsRow,
    buildWarehouseReportSummary,
    formatNumber,
} from '@/views/cromis/reports/warehouse_reports/warehouseReportsHelpers'
import { jsPDF as JsPDF } from 'jspdf'
import 'jspdf-autotable'
import { $themeConfig } from '@themeConfig'
import { addReportHeader, createReportFooterCallback } from '@core/utils/pdfReportHelpers'
import exportTableToExcel from '@core/utils/excelReportHelpers'
import logo from '@/assets/images/logo/logo3.png'
import {
    ref, computed, nextTick, onMounted, onUnmounted, watch,
} from '@vue/composition-api'

export default {
    components: {
        BRow,
        BCol,
        BCard,
        BCardHeader,
        BCardBody,
        BTable,
        BForm,
        BFormGroup,
        BButton,
        BFormCheckbox,
        BFormSelect,
        BFormSelectOption,
        BFormDatepicker,
        BAlert,
        BSpinner,
        ValidationObserver,
        ValidationProvider,
    },
    setup() {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        const isSuperUser = ref(
            userData.super_user === true
      || (userData.roles && userData.roles[0] && (
          userData.roles[0].id === 1
        || (typeof userData.roles[0].title === 'string' && userData.roles[0].title.toLowerCase().includes('super'))
      )),
        )
        const locId = ref(userData.station ? userData.station.location : null)
        const observer = ref(null)
        const parametersRefreshKey = ref(0)
        const showTfSummary = ref(false)
        const showStackSummary = ref(false)
        const filter = ref({
            region_id: null,
            warehouse_id: [],
            start_date: null,
            end_date: null,
        })
        const companyName = $themeConfig.app.appName
        const { companyAddress } = $themeConfig.app
        const errorMessage = ref(null)
        const CROMIS_STORE_MODULE_NAME = 'cromis-warehouse-reports'

        const selectedReportKey = ref(null)
        const reportConfigMap = {
            warehouse_stock_position_by_internal_grade: {
                key: 'warehouse_stock_position_by_internal_grade',
                label: 'Warehouse Stock Position By Internal Grade',
                requiresDateRange: false,
            },
            warehouse_receiving_report: {
                key: 'warehouse_receiving_report',
                label: 'Warehouse Receiving Report',
                requiresDateRange: true,
                allowMultipleWarehouses: false,
                columns: [
                    {
                        key: 'TF_creation', label: 'TF Creation', sortable: true, summarize: false,
                    },
                    {
                        key: 'Region_Name', label: 'Region', sortable: true, summarize: false,
                    },
                    {
                        key: 'TF_Number', label: 'TF Number', sortable: true, summarize: false,
                    },
                    {
                        key: 'Source_name', label: 'Source', sortable: true, summarize: false,
                    },
                    {
                        key: 'Destination_Name', label: 'Destination', sortable: true, summarize: false,
                    },
                    {
                        key: 'Truck_No', label: 'Truck No', sortable: true, summarize: false,
                    },
                    {
                        key: 'barcode', label: 'Barcode', sortable: true, summarize: false,
                    },
                    {
                        key: 'purchase_mass', label: 'Purchase Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loaded_at', label: 'Loaded At', sortable: true, summarize: false,
                    },
                    {
                        key: 'Dispatch_Mass', label: 'Dispatch Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Received_at', label: 'Received At', sortable: true, summarize: false,
                    },
                    {
                        key: 'Receive_Mass', label: 'Receive Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loss_Market', label: 'Market Loss', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loss_Transporter', label: 'Transporter Loss', sortable: true, formatter: value => formatNumber(value),
                    },
                ],
                summaryColumns: [
                    {
                        key: 'TF_creation', label: 'TF Creation', sortable: true, summarize: false,
                    },
                    {
                        key: 'Region_Name', label: 'Region', sortable: true, summarize: false,
                    },
                    {
                        key: 'TF_Number', label: 'TF Number', sortable: true, summarize: false,
                    },
                    {
                        key: 'Source_name', label: 'Source', sortable: true, summarize: false,
                    },
                    {
                        key: 'Destination_Name', label: 'Destination', sortable: true, summarize: false,
                    },
                    {
                        key: 'Truck_No', label: 'Truck No', sortable: true, summarize: false,
                    },
                    {
                        key: 'barcode_count', label: 'Barcodes', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'purchase_mass', label: 'Purchase Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loaded_at', label: 'Loaded At', sortable: true, summarize: false,
                    },
                    {
                        key: 'Dispatch_Mass', label: 'Dispatch Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Received_at', label: 'Received At', sortable: true, summarize: false,
                    },
                    {
                        key: 'Receive_Mass', label: 'Receive Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loss_Market', label: 'Market Loss', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loss_Transporter', label: 'Transporter Loss', sortable: true, formatter: value => formatNumber(value),
                    },
                ],
            },
            warehouse_dispatch_report: {
                key: 'warehouse_dispatch_report',
                label: 'Warehouse Dispatch Report',
                requiresDateRange: true,
                allowMultipleWarehouses: false,
                columns: [
                    {
                        key: 'TF_creation', label: 'TF Creation', sortable: true, summarize: false,
                    },
                    {
                        key: 'Region_Name', label: 'Region', sortable: true, summarize: false,
                    },
                    {
                        key: 'TF_Number', label: 'TF Number', sortable: true, summarize: false,
                    },
                    {
                        key: 'Source_name', label: 'Source', sortable: true, summarize: false,
                    },
                    {
                        key: 'Destination_Name', label: 'Destination', sortable: true, summarize: false,
                    },
                    {
                        key: 'Truck_No', label: 'Truck No', sortable: true, summarize: false,
                    },
                    {
                        key: 'barcode', label: 'Barcode', sortable: true, summarize: false,
                    },
                    {
                        key: 'purchase_mass', label: 'Purchase Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loaded_at', label: 'Loaded At', sortable: true, summarize: false,
                    },
                    {
                        key: 'Dispatch_Mass', label: 'Dispatch Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Received_at', label: 'Received At', sortable: true, summarize: false,
                    },
                    {
                        key: 'Receive_Mass', label: 'Receive Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loss_Market', label: 'Market Loss', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loss_Transporter', label: 'Transporter Loss', sortable: true, formatter: value => formatNumber(value),
                    },
                ],
                summaryColumns: [
                    {
                        key: 'TF_creation', label: 'TF Creation', sortable: true, summarize: false,
                    },
                    {
                        key: 'Region_Name', label: 'Region', sortable: true, summarize: false,
                    },
                    {
                        key: 'TF_Number', label: 'TF Number', sortable: true, summarize: false,
                    },
                    {
                        key: 'Source_name', label: 'Source', sortable: true, summarize: false,
                    },
                    {
                        key: 'Destination_Name', label: 'Destination', sortable: true, summarize: false,
                    },
                    {
                        key: 'Truck_No', label: 'Truck No', sortable: true, summarize: false,
                    },
                    {
                        key: 'barcode_count', label: 'Barcodes', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'purchase_mass', label: 'Purchase Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loaded_at', label: 'Loaded At', sortable: true, summarize: false,
                    },
                    {
                        key: 'Dispatch_Mass', label: 'Dispatch Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Received_at', label: 'Received At', sortable: true, summarize: false,
                    },
                    {
                        key: 'Receive_Mass', label: 'Receive Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loss_Market', label: 'Market Loss', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Loss_Transporter', label: 'Transporter Loss', sortable: true, formatter: value => formatNumber(value),
                    },
                ],
            },
            warehouse_stack_grades: {
                key: 'warehouse_stack_grades',
                label: 'Warehouse Stack By Grades',
                requiresDateRange: true,
                columns: [
                    {
                        key: 'barcode', label: 'Barcode', sortable: true, summarize: false,
                    },
                    {
                        key: 'region_name', label: 'Region', sortable: true, summarize: false,
                    },
                    {
                        key: 'Warehouse_Name', label: 'Warehouse', sortable: true, summarize: false,
                    },
                    {
                        key: 'stack_name', label: 'Stack', sortable: true, summarize: false,
                    },
                    {
                        key: 'stacking_date', label: 'Stacking Date', sortable: true, summarize: false,
                    },
                    {
                        key: 'grade_name', label: 'Grade', sortable: true, summarize: false,
                    },
                    {
                        key: 'Category', label: 'Category', sortable: true, summarize: false,
                    },
                    {
                        key: 'Stack_Mass', label: 'Stack Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Purchase_Mass', label: 'Purchase Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                ],
                summaryColumns: [
                    {
                        key: 'region_name', label: 'Region', sortable: true, summarize: false,
                    },
                    {
                        key: 'Warehouse_Name', label: 'Warehouse', sortable: true, summarize: false,
                    },
                    {
                        key: 'stack_name', label: 'Stack', sortable: true, summarize: false,
                    },
                    {
                        key: 'stacking_date', label: 'Stacking Date', sortable: true, summarize: false,
                    },
                    {
                        key: 'grade_name', label: 'Grade', sortable: true, summarize: false,
                    },
                    {
                        key: 'Category', label: 'Category', sortable: true, summarize: false,
                    },
                    {
                        key: 'barcode_count', label: 'Barcodes', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Stack_Mass', label: 'Stack Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Purchase_Mass', label: 'Purchase Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                ],
            },
            stacked_bales_by_date: {
                key: 'stacked_bales_by_date',
                label: 'Stacked Bales by Date',
                requiresDateRange: true,
            },
            warehouse_tf_movement_report: {
                key: 'warehouse_tf_movement_report',
                label: 'Warehouse TF Movement Report',
                requiresDateRange: false,
            },
        }
        const reportOptions = ref(Object.values(reportConfigMap).map(config => ({
            key: config.key,
            label: config.label,
        })))

        if (!store.hasModule(CROMIS_STORE_MODULE_NAME)) {
            store.registerModule(CROMIS_STORE_MODULE_NAME, warehouseReportsStoreModule)
        }

        onUnmounted(() => {
            if (store.hasModule(CROMIS_STORE_MODULE_NAME)) {
                store.unregisterModule(CROMIS_STORE_MODULE_NAME)
            }
        })

        const {
            records,
            loading,
            totalRecords,
            fetchReport,
        } = useWarehouseReportsList()

        const warehouseOptions = ref([])
        const regionOptions = ref([])

        const formatDate = value => {
            if (!value) return null
            if (typeof value === 'string') return value
            if (value instanceof Date) return value.toISOString().split('T')[0]
            return String(value).split('T')[0]
        }

        const currentReportConfig = computed(() => {
            const key = selectedReportKey.value
            return key && reportConfigMap[key] ? reportConfigMap[key] : reportConfigMap.warehouse_stock_position_by_internal_grade
        })
        const supportsTfSummaryReport = computed(() => [
            'warehouse_receiving_report',
            'warehouse_dispatch_report',
        ].includes(selectedReportKey.value))
        const isTfSummaryMode = computed(() => supportsTfSummaryReport.value && showTfSummary.value)
        const supportsStackSummaryReport = computed(() => selectedReportKey.value === 'warehouse_stack_grades')
        const isStackSummaryMode = computed(() => supportsStackSummaryReport.value && showStackSummary.value)
        const requiresDateRange = computed(() => currentReportConfig.value.requiresDateRange === true || supportsStackSummaryReport.value)
        const allowsMultipleWarehouses = computed(() => currentReportConfig.value.allowMultipleWarehouses !== false)
        const selectedWarehouseIds = computed(() => {
            if (Array.isArray(filter.value.warehouse_id)) {
                return filter.value.warehouse_id.filter(id => id !== null && id !== undefined && String(id).trim() !== '')
            }
            if (filter.value.warehouse_id === null || filter.value.warehouse_id === undefined || String(filter.value.warehouse_id).trim() === '') {
                return []
            }
            return [filter.value.warehouse_id]
        })
        const parseNumber = value => {
            if (value === null || value === undefined || value === '') return null
            const number = Number(String(value).replace(/,/g, ''))
            return Number.isNaN(number) ? null : number
        }
        const sumField = (items, key) => {
            const result = items.reduce((acc, item) => {
                const value = parseNumber(item ? item[key] : null)
                if (value === null) return acc
                return {
                    total: acc.total + value,
                    hasValue: true,
                }
            }, {
                total: 0,
                hasValue: false,
            })
            return result.hasValue ? result.total : ''
        }
        const firstValue = (items, key) => {
            const record = items.find(item => item && item[key] !== null && item[key] !== undefined && String(item[key]).trim() !== '')
            return record ? record[key] : ''
        }
        const firstValueFromKeys = (items, keys) => {
            for (let i = 0; i < items.length; i += 1) {
                const item = items[i]
                if (item) {
                    for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
                        const key = keys[keyIndex]
                        if (item[key] !== null && item[key] !== undefined && String(item[key]).trim() !== '') {
                            return item[key]
                        }
                    }
                }
            }
            return ''
        }
        const getTfNumber = record => firstValueFromKeys([record], [
            'TF_Number',
            'tfNumber',
            'tf_number',
            'system_tf_number',
            'system_tf_no',
        ])
        const countTfNumbers = items => Array.from(new Set(
            items
                .map(item => getTfNumber(item))
                .map(value => (value !== null && value !== undefined ? String(value).trim() : ''))
                .filter(value => value !== '' && value.toUpperCase() !== 'N/A'),
        )).length
        const uniqueValues = (items, key) => Array.from(new Set(
            items
                .map(item => (item && item[key] !== null && item[key] !== undefined ? String(item[key]).trim() : ''))
                .filter(value => value !== ''),
        )).join(', ')
        const buildTfSummaryRecords = sourceRecords => {
            if (!Array.isArray(sourceRecords) || sourceRecords.length === 0) return []

            const groups = new Map()
            sourceRecords.forEach(record => {
                const rawTfNumber = getTfNumber(record)
                const tfNumber = rawTfNumber !== null && rawTfNumber !== undefined && String(rawTfNumber).trim() !== ''
                    ? String(rawTfNumber).trim()
                    : 'N/A'
                const current = groups.get(tfNumber) || []
                groups.set(tfNumber, [...current, record])
            })

            return Array.from(groups.values()).map(group => {
                const barcodeCount = group.filter(item => item && item.barcode !== null && item.barcode !== undefined && String(item.barcode).trim() !== '').length
                return {
                    TF_creation: firstValue(group, 'TF_creation'),
                    Region_Name: firstValue(group, 'Region_Name'),
                    TF_Number: firstValueFromKeys(group, [
                        'TF_Number',
                        'tfNumber',
                        'tf_number',
                        'system_tf_number',
                        'system_tf_no',
                    ]) || 'N/A',
                    Source_name: firstValue(group, 'Source_name'),
                    Destination_Name: firstValue(group, 'Destination_Name'),
                    Truck_No: uniqueValues(group, 'Truck_No'),
                    barcode_count: barcodeCount || group.length,
                    purchase_mass: sumField(group, 'purchase_mass'),
                    Loaded_at: firstValue(group, 'Loaded_at'),
                    Dispatch_Mass: sumField(group, 'Dispatch_Mass'),
                    Received_at: firstValue(group, 'Received_at'),
                    Receive_Mass: sumField(group, 'Receive_Mass'),
                    Loss_Market: sumField(group, 'Loss_Market'),
                    Loss_Transporter: sumField(group, 'Loss_Transporter'),
                }
            })
        }
        const getStackGroupKey = record => {
            if (!record) return 'stack:N/A'
            if (record.stack_id !== null && record.stack_id !== undefined && String(record.stack_id).trim() !== '') {
                return `stack:${record.stack_id}`
            }

            const fallbackKey = [
                record.Warehouse_Name,
                record.stack_name,
                record.stacking_date,
            ].map(value => (value !== null && value !== undefined ? String(value).trim() : '')).join('|')

            return fallbackKey.replace(/\|/g, '') ? `stack:${fallbackKey}` : 'stack:N/A'
        }
        const buildStackSummaryRecords = sourceRecords => {
            if (!Array.isArray(sourceRecords) || sourceRecords.length === 0) return []

            const groups = new Map()
            sourceRecords.forEach(record => {
                const stackKey = getStackGroupKey(record)
                const current = groups.get(stackKey) || []
                groups.set(stackKey, [...current, record])
            })

            return Array.from(groups.values()).map(group => {
                const barcodeCount = group.filter(item => item && item.barcode !== null && item.barcode !== undefined && String(item.barcode).trim() !== '').length
                return {
                    region_name: uniqueValues(group, 'region_name'),
                    Warehouse_Name: uniqueValues(group, 'Warehouse_Name'),
                    stack_name: firstValue(group, 'stack_name') || firstValue(group, 'stack_id') || 'N/A',
                    stacking_date: uniqueValues(group, 'stacking_date'),
                    grade_name: uniqueValues(group, 'grade_name'),
                    Category: uniqueValues(group, 'Category'),
                    barcode_count: barcodeCount || group.length,
                    Stack_Mass: sumField(group, 'Stack_Mass'),
                    Purchase_Mass: sumField(group, 'Purchase_Mass'),
                }
            })
        }
        const displayedRecords = computed(() => {
            if (isTfSummaryMode.value) return buildTfSummaryRecords(records.value)
            if (isStackSummaryMode.value) return buildStackSummaryRecords(records.value)
            return records.value
        })
        const displayedRecordCount = computed(() => displayedRecords.value.length)

        const currentColumns = computed(() => {
            if (!displayedRecords.value.length) return []
            if ((isTfSummaryMode.value || isStackSummaryMode.value) && currentReportConfig.value.summaryColumns) {
                return currentReportConfig.value.summaryColumns
            }
            if (currentReportConfig.value.columns && currentReportConfig.value.columns.length) {
                return currentReportConfig.value.columns
            }
            return buildWarehouseReportColumns(displayedRecords.value)
        })
        const warehouseTotalsRow = computed(() => {
            const totalsRow = buildWarehouseReportTotalsRow(displayedRecords.value, currentColumns.value)
            if (
                totalsRow
                && [
                    'warehouse_receiving_report',
                    'warehouse_dispatch_report',
                ].includes(selectedReportKey.value)
                && isTfSummaryMode.value
            ) {
                totalsRow.TF_Number = formatNumber(countTfNumbers(displayedRecords.value))
            }
            return totalsRow
        })
        const tableItems = computed(() => {
            if (warehouseTotalsRow.value) {
                return [warehouseTotalsRow.value, ...displayedRecords.value]
            }
            return displayedRecords.value
        })
        const sortCompare = (a, b) => {
            const aIsSummary = a && a.summaryRow
            const bIsSummary = b && b.summaryRow
            if (aIsSummary && bIsSummary) return 0
            if (aIsSummary) return -1
            if (bIsSummary) return 1
            return null
        }
        const tbodyTrClass = (item, type) => {
            if (type !== 'row' || !item) return ''
            return item.summaryRow ? 'table-active' : ''
        }
        const warehouseSummary = computed(() => buildWarehouseReportSummary(displayedRecords.value, currentColumns.value))
        const summaryTotalLabel = computed(() => {
            if (isTfSummaryMode.value) return 'Total TFs'
            if (isStackSummaryMode.value) return 'Total Stacks'
            return 'Total Records'
        })
        const warehouseSummaryItems = computed(() => {
            if (!warehouseSummary.value) return []
            const items = [
                { label: summaryTotalLabel.value, value: warehouseSummary.value.totalRecords },
            ]
            currentColumns.value.forEach(column => {
                const value = warehouseSummary.value.totals[column.key]
                if (value !== undefined && value !== '') {
                    items.push({ label: column.label || column.key, value })
                }
            })
            return items
        })

        const hasSelectedWarehouses = computed(() => selectedWarehouseIds.value.length > 0)

        const isGenerateDisabled = computed(() => {
            if (loading.value || !selectedReportKey.value) return true
            if (isSuperUser.value && !filter.value.region_id) return true
            if (!isSuperUser.value && !locId.value) return true
            if (!hasSelectedWarehouses.value) return true
            if (requiresDateRange.value) {
                if (!filter.value.start_date || !filter.value.end_date) return true
            }
            return false
        })

        const loadRegions = async () => {
            try {
                const response = await store.dispatch('cromis-warehouse-reports/locations')
                regionOptions.value = response.data.locations || response.data.data || response.data
            } catch (error) {
                regionOptions.value = []
            }
        }

        const loadWarehouses = async (locationId = null) => {
            const params = { storage_only: true }
            if (locationId) {
                params.location_id = locationId
            } else if (!isSuperUser.value && locId.value) {
                params.location_id = locId.value.id ?? locId.value
            } else if (isSuperUser.value) {
                warehouseOptions.value = []
                return
            }
            try {
                const response = await store.dispatch('cromis-warehouse-reports/warehouses', params)
                warehouseOptions.value = response.data.warehouse_names || response.data.warehouses || response.data.data || response.data
            } catch (error) {
                warehouseOptions.value = []
            }
        }

        const changeRegion = async region => {
            filter.value.region_id = region
            filter.value.warehouse_id = allowsMultipleWarehouses.value ? [] : null
            if (!region) {
                warehouseOptions.value = []
                return
            }
            await loadWarehouses(region)
        }

        watch(selectedReportKey, async newKey => {
            records.value = []
            totalRecords.value = 0
            errorMessage.value = null
            filter.value.region_id = !isSuperUser.value && locId.value ? (locId.value.id ?? locId.value) : null
            filter.value.warehouse_id = allowsMultipleWarehouses.value ? [] : null
            filter.value.start_date = null
            filter.value.end_date = null
            showTfSummary.value = false
            showStackSummary.value = false
            warehouseOptions.value = []
            parametersRefreshKey.value += 1

            if (newKey && !isSuperUser.value && filter.value.region_id) {
                await loadWarehouses(filter.value.region_id)
            }

            await nextTick()
            if (observer.value) observer.value.reset()
        })

        const generateReport = async () => {
            errorMessage.value = null

            if (isSuperUser.value && !filter.value.region_id) {
                errorMessage.value = 'Please select a region.'
                return
            }

            if (!isSuperUser.value && !locId.value) {
                errorMessage.value = 'Location is not set for your account.'
                return
            }

            if (!hasSelectedWarehouses.value) {
                errorMessage.value = 'Please select a warehouse.'
                return
            }

            if (requiresDateRange.value) {
                if (!filter.value.start_date || !filter.value.end_date) {
                    errorMessage.value = 'Please select both start and end dates.'
                    return
                }
            }

            const params = {
                reportType: selectedReportKey.value,
                warehouse_id: allowsMultipleWarehouses.value
                    ? selectedWarehouseIds.value.join(',')
                    : String(selectedWarehouseIds.value[0]),
            }

            if (requiresDateRange.value) {
                params.start_date = formatDate(filter.value.start_date)
                params.end_date = formatDate(filter.value.end_date)
            }

            await fetchReport(params)
        }

        const getReportFileNameParts = () => {
            const fileNameParts = [currentReportConfig.value.label.replace(/[^a-zA-Z0-9]/g, '_')]
            if (supportsTfSummaryReport.value) {
                fileNameParts.push(isTfSummaryMode.value ? 'summary_by_tf' : 'detailed')
            }
            if (supportsStackSummaryReport.value) {
                fileNameParts.push(isStackSummaryMode.value ? 'summary_by_stack' : 'detailed_barcodes')
            }
            if (requiresDateRange.value) {
                fileNameParts.push(formatDate(filter.value.start_date), formatDate(filter.value.end_date))
            }
            return fileNameParts.filter(Boolean)
        }

        const getFormattedCellValue = (item, column) => {
            const rawValue = item[column.key]
            if (column.formatter) return column.formatter(rawValue)
            return rawValue === null || rawValue === undefined ? '' : rawValue
        }

        const getReportHeaderDetails = () => {
            const title = currentReportConfig.value.label
            const producer = userData.fullName || userData.username || 'Unknown User'
            const executionDateTime = new Date().toLocaleString()

            let warehouseLabel = 'N/A'
            if (hasSelectedWarehouses.value) {
                if (allowsMultipleWarehouses.value) {
                    const selectedIds = selectedWarehouseIds.value.map(id => String(id))
                    const selectedWarehouses = warehouseOptions.value.filter(w => selectedIds.includes(String(w.id)))
                    warehouseLabel = selectedWarehouses.map(w => w.name).join(', ')
                } else {
                    const selectedWarehouse = warehouseOptions.value.find(w => String(w.id) === String(selectedWarehouseIds.value[0]))
                    warehouseLabel = selectedWarehouse ? selectedWarehouse.name : 'N/A'
                }
            }

            const reportParameters = [
                { label: 'Warehouse', value: warehouseLabel },
            ]

            if (supportsTfSummaryReport.value) {
                reportParameters.push({
                    label: 'View',
                    value: isTfSummaryMode.value ? 'Summary by TF' : 'Detailed',
                })
            }

            if (supportsStackSummaryReport.value) {
                reportParameters.push({
                    label: 'View',
                    value: isStackSummaryMode.value ? 'Summary by Stack' : 'Detailed Barcodes',
                })
            }

            if (requiresDateRange.value) {
                reportParameters.push({
                    label: 'Period',
                    value: `From ${formatDate(filter.value.start_date)} to ${formatDate(filter.value.end_date)}`,
                })
            }

            const reportParametersRight = warehouseSummaryItems.value.map(item => ({
                label: item.label,
                value: item.value,
            }))

            return {
                title,
                producer,
                executionDateTime,
                reportParameters,
                reportParametersRight,
            }
        }

        const exportPdf = () => {
            const doc = new JsPDF('landscape')
            const {
                title,
                producer,
                executionDateTime,
                reportParameters,
                reportParametersRight,
            } = getReportHeaderDetails()

            const headerStartY = addReportHeader(doc, {
                logo,
                companyName,
                companyAddress,
                reportTitle: title,
                reportParameters,
                reportParametersRight,
            })

            const head = currentColumns.value.map(column => column.label)
            const body = displayedRecords.value.map(item => currentColumns.value.map(column => getFormattedCellValue(item, column)))

            doc.autoTable({
                head: [head],
                body,
                startY: headerStartY,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                margin: { left: 14, right: 14 },
                didDrawPage: createReportFooterCallback(doc, {
                    generatedBy: producer,
                    executionDateTime,
                }),
            })

            const fileName = `${getReportFileNameParts().join('_')}.pdf`
            doc.save(fileName)
        }

        const exportExcel = () => {
            const {
                title,
                producer,
                executionDateTime,
                reportParameters,
                reportParametersRight,
            } = getReportHeaderDetails()
            exportTableToExcel({
                title,
                fileName: getReportFileNameParts().join('_'),
                companyName,
                companyAddress,
                reportParameters,
                reportParametersRight,
                generatedBy: producer,
                executionDateTime,
                columns: currentColumns.value,
                items: tableItems.value,
                getCellValue: getFormattedCellValue,
            })
        }

        onMounted(async () => {
            await loadRegions()

            if (isSuperUser.value) return

            if (locId.value) {
                filter.value.region_id = locId.value.id ?? locId.value
                await loadWarehouses(filter.value.region_id)
            } else {
                await loadWarehouses()
            }
        })

        return {
            selectedReportKey,
            observer,
            parametersRefreshKey,
            reportOptions,
            currentReportConfig,
            currentColumns,
            isGenerateDisabled,
            hasSelectedWarehouses,
            allowsMultipleWarehouses,
            supportsTfSummaryReport,
            isTfSummaryMode,
            showTfSummary,
            supportsStackSummaryReport,
            isStackSummaryMode,
            showStackSummary,
            requiresDateRange,
            records,
            displayedRecordCount,
            loading,
            totalRecords,
            filter,
            warehouseOptions,
            regionOptions,
            errorMessage,
            generateReport,
            changeRegion,
            exportPdf,
            exportExcel,
            isSuperUser,
            tableItems,
            sortCompare,
            tbodyTrClass,
            warehouseSummaryItems,
        }
    },
}
</script>
